import Phaser from 'phaser';
import type {
  GameRuntime,
  GameRuntimeConfig,
  GameRuntimeEvents,
  GameRuntimeState,
} from '@adgf/game-engine';

const COLOR_BG = 0x0f0e1a;
const COLOR_TRAY_BG = 0x1a1930;
const COLOR_TILE_BG = 0x2a2848;
const COLOR_TILE_BORDER = 0x555588;
const COLOR_ACCENT = 0xff6b9d;
const COLOR_ACCENT_STR = '#ff6b9d';
const COLOR_TEXT = '#ffffff';
const COLOR_TEXT_DIM = '#8888aa';
const COLOR_SUCCESS = '#4ade80';
const COLOR_DANGER = '#ef4444';

const TILE_COLORS = [
  0xff6b9d, 0x4ecdc4, 0xffd93d, 0x6bcf7f,
  0x4d96ff, 0xff8c42, 0xb084ff, 0xf87171,
  0xff6b6b, 0x4ecdc4, 0xffe66d, 0x7bed9f,
] as const;

const TRAY_SIZE = 7;
const GAME_WIDTH = 420;
const GAME_HEIGHT = 720;

interface Tile {
  id: number;
  shape: number;
  color: number;
  x: number;
  y: number;
  layer: number;
  gameObject: Phaser.GameObjects.Container | null;
  inTray: boolean;
  cleared: boolean;
}

interface SceneCallbacks {
  onTileTap: () => void;
  onTileClear: () => void;
  onPuzzleSolved: (level: number, score: number) => void;
  onPuzzleFailed: (pct: number, score: number) => void;
  onStart: () => void;
  onLevelComplete: (level: number) => void;
}

interface LevelConfig {
  level: number;
  layers: { rows: number; cols: number; offsetX: number; offsetY: number }[];
  types: number; // number of different tile types
  tilesPerType: number; // tiles per type (must be multiple of 3)
}

const LEVELS: LevelConfig[] = [
  {
    level: 1,
    layers: [
      { rows: 4, cols: 3, offsetX: 0, offsetY: 0 },
      { rows: 3, cols: 2, offsetX: 36, offsetY: 24 },
    ],
    types: 6,
    tilesPerType: 3,
  },
  {
    level: 2,
    layers: [
      { rows: 5, cols: 3, offsetX: 0, offsetY: 0 },
      { rows: 4, cols: 2, offsetX: 40, offsetY: 20 },
      { rows: 2, cols: 2, offsetX: 80, offsetY: 50 },
    ],
    types: 8,
    tilesPerType: 3,
  },
  {
    level: 3,
    layers: [
      { rows: 5, cols: 4, offsetX: 0, offsetY: 0 },
      { rows: 4, cols: 3, offsetX: 30, offsetY: 20 },
      { rows: 3, cols: 2, offsetX: 60, offsetY: 40 },
      { rows: 2, cols: 2, offsetX: 90, offsetY: 60 },
    ],
    types: 10,
    tilesPerType: 3,
  },
];

function seededRandom(seed: number): () => number {
  let a = seed;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function getDailySeed(): number {
  const now = new Date();
  return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
}

function getStreakKey(): string { return `adgf_dtm_streak_${getDailySeed()}`; }
function getBestKey(): string { return `adgf_dtm_best_${getDailySeed()}`; }

function getStoredStreak(): number {
  try { return parseInt(localStorage.getItem(getStreakKey()) || '0', 10); } catch { return 0; }
}
function saveStreak(s: number): void {
  try { localStorage.setItem(getStreakKey(), String(s)); } catch { /* ignore */ }
}
function getBest(): number {
  try { return parseInt(localStorage.getItem(getBestKey()) || '0', 10); } catch { return 0; }
}
function saveBest(pct: number): void {
  try {
    const prev = parseInt(localStorage.getItem(getBestKey()) || '0', 10);
    if (pct > prev) localStorage.setItem(getBestKey(), String(pct));
  } catch { /* ignore */ }
}

class DailyTileMatchScene extends Phaser.Scene {
  private tiles: Tile[] = [];
  private traySlots: Tile[] = [];
  private nextTileId = 0;
  private cb: SceneCallbacks;
  private playing = false;
  private solved = false;
  private level = 1;
  private score = 0;
  private tileSize = 60;
  private tileGap = 6;

  constructor(cb: SceneCallbacks) {
    super('DailyTileMatchScene');
    this.cb = cb;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLOR_BG);
    this.tiles = [];
    this.traySlots = [];
    this.nextTileId = 0;
    this.playing = false;
    this.solved = false;
    this.level = 1;
    this.score = 0;

    this.buildDecorations();
    this.buildHeader();
    this.buildTrayBackground();
    this.buildStartOverlay();
  }

  private buildDecorations(): void {
    // Soft ambient circles
    const circles = [
      { x: 80, y: 150, r: 120, c: 0xff6b9d, a: 0.04 },
      { x: 350, y: 300, r: 100, c: 0x4ecdc4, a: 0.04 },
      { x: 100, y: 500, r: 140, c: 0xb084ff, a: 0.03 },
      { x: 360, y: 600, r: 90, c: 0xffd93d, a: 0.03 },
    ];
    for (const c of circles) {
      this.add.circle(c.x, c.y, c.r, c.c, c.a).setDepth(-10);
    }
  }

  private buildHeader(): void {
    const { width } = this.scale;

    // Level indicator
    const levelText = this.add.text(width / 2, 28, `LEVEL ${this.level}`, {
      fontSize: '14px',
      color: COLOR_ACCENT_STR,
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
      letterSpacing: 2,
    });
    levelText.setOrigin(0.5);

    // Score
    const scoreText = this.add.text(width / 2, 50, `Score: ${this.score}`, {
      fontSize: '18px',
      color: COLOR_TEXT,
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
    });
    scoreText.setOrigin(0.5);
    scoreText.setName('scoreText');
  }

  private updateScore(): void {
    const scoreText = this.children.getByName('scoreText') as Phaser.GameObjects.Text;
    scoreText?.setText(`Score: ${this.score}`);
  }

  private buildBoard(): void {
    const { width, height } = this.scale;
    const levelConfig = LEVELS[this.level - 1] || LEVELS[0];
    const rand = seededRandom(getDailySeed() + this.level * 1000);

    const totalTiles = levelConfig.types * levelConfig.tilesPerType;

    // Create type pool
    const typePool: number[] = [];
    for (let t = 0; t < levelConfig.types; t++) {
      for (let i = 0; i < levelConfig.tilesPerType; i++) {
        typePool.push(t);
      }
    }
    // Shuffle
    for (let i = typePool.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [typePool[i], typePool[j]] = [typePool[j], typePool[i]];
    }

    let typeIdx = 0;
    const boardCenterX = width / 2;
    const boardCenterY = height / 2 - 30;

    for (let layerIdx = 0; layerIdx < levelConfig.layers.length; layerIdx++) {
      const layer = levelConfig.layers[layerIdx];
      const totalW = layer.cols * (this.tileSize + this.tileGap) - this.tileGap;
      const totalH = layer.rows * (this.tileSize + this.tileGap) - this.tileGap;

      for (let row = 0; row < layer.rows; row++) {
        for (let col = 0; col < layer.cols; col++) {
          if (typeIdx >= typePool.length) break;
          const shapeIdx = typePool[typeIdx++];
          const x = boardCenterX - totalW / 2 + col * (this.tileSize + this.tileGap) + layer.offsetX;
          const y = boardCenterY - totalH / 2 + row * (this.tileSize + this.tileGap) + layer.offsetY;

          this.tiles.push({
            id: this.nextTileId++,
            shape: shapeIdx % TILE_COLORS.length,
            color: shapeIdx % TILE_COLORS.length,
            x, y, layer: layerIdx,
            gameObject: null, inTray: false, cleared: false,
          });
        }
      }
    }

    // If we have fewer tiles than planned due to grid layout, pad with more types
    while (typeIdx < totalTiles) {
      const shapeIdx = Math.floor(rand() * levelConfig.types);
      const layerIdx = Math.floor(rand() * levelConfig.layers.length);
      const layer = levelConfig.layers[layerIdx];
      const row = Math.floor(rand() * layer.rows);
      const col = Math.floor(rand() * layer.cols);
      const totalW = layer.cols * (this.tileSize + this.tileGap) - this.tileGap;
      const totalH = layer.rows * (this.tileSize + this.tileGap) - this.tileGap;
      const x = boardCenterX - totalW / 2 + col * (this.tileSize + this.tileGap) + layer.offsetX;
      const y = boardCenterY - totalH / 2 + row * (this.tileSize + this.tileGap) + layer.offsetY;

      this.tiles.push({
        id: this.nextTileId++,
        shape: shapeIdx % TILE_COLORS.length,
        color: shapeIdx % TILE_COLORS.length,
        x: x + (rand() - 0.5) * 20,
        y: y + (rand() - 0.5) * 20,
        layer: layerIdx,
        gameObject: null, inTray: false, cleared: false,
      });
      typeIdx++;
    }

    this.tiles.sort((a, b) => a.layer - b.layer);
    for (const tile of this.tiles) this.createTileVisual(tile);
    this.updateClickableState();
  }

  private createTileVisual(tile: Tile): void {
    const s = this.tileSize;
    const container = this.add.container(tile.x, tile.y);

    // Tile background — rounded look with border
    const bg = this.add.rectangle(0, 0, s, s, COLOR_TILE_BG, 1);
    bg.setStrokeStyle(2, COLOR_TILE_BORDER, 0.9);
    container.add(bg);

    // Colored shape
    const shapeColor = TILE_COLORS[tile.color];
    const innerS = s * 0.45;
    let shapeObj: Phaser.GameObjects.Shape;

    switch (tile.shape % 8) {
      case 0: // circle
        shapeObj = this.add.circle(0, 0, innerS / 2, shapeColor);
        break;
      case 1: // square
        shapeObj = this.add.rectangle(0, 0, innerS, innerS, shapeColor);
        break;
      case 2: // triangle
        shapeObj = this.add.triangle(0, 0, 0, -innerS / 2, innerS / 2, innerS / 2, -innerS / 2, innerS / 2, shapeColor);
        break;
      case 3: // diamond
        shapeObj = this.add.rectangle(0, 0, innerS, innerS, shapeColor);
        shapeObj.setAngle(45);
        break;
      case 4: // star
        shapeObj = this.add.star(0, 0, 5, innerS * 0.3, innerS * 0.55, shapeColor);
        break;
      case 5: // hexagon (use circle for simplicity)
        shapeObj = this.add.circle(0, 0, innerS / 2, shapeColor);
        break;
      case 6: // rounded square (use circle)
        shapeObj = this.add.circle(0, 0, innerS / 2, shapeColor);
        break;
      default: // cross
        shapeObj = this.add.rectangle(0, 0, innerS, innerS * 0.35, shapeColor);
        break;
    }
    container.add(shapeObj);

    // Gloss highlight (top-left)
    const gloss = this.add.circle(-innerS * 0.25, -innerS * 0.25, innerS * 0.12, 0xffffff, 0.3);
    container.add(gloss);

    container.setDepth(tile.layer * 10);
    container.setSize(s, s);
    container.setInteractive(new Phaser.Geom.Rectangle(0, 0, s, s), Phaser.Geom.Rectangle.Contains, true);
    container.on('pointerdown', () => this.onTileTap(tile));
    tile.gameObject = container;
  }

  private isTileCovered(tile: Tile): boolean {
    const s = this.tileSize;
    for (const other of this.tiles) {
      if (other.cleared || other.inTray || other.id === tile.id) continue;
      if (other.layer <= tile.layer) continue;
      if (Math.abs(other.x - tile.x) < s * 0.6 && Math.abs(other.y - tile.y) < s * 0.6) return true;
    }
    return false;
  }

  private updateClickableState(): void {
    for (const tile of this.tiles) {
      if (!tile.gameObject || tile.inTray || tile.cleared) continue;
      tile.gameObject.setAlpha(this.isTileCovered(tile) ? 0.3 : 1.0);
    }
  }

  private onTileTap(tile: Tile): void {
    if (!this.playing || tile.inTray || tile.cleared) return;
    if (this.isTileCovered(tile)) return;
    if (this.traySlots.length >= TRAY_SIZE) return;

    tile.inTray = true;
    const trayIndex = this.traySlots.length;
    this.traySlots.push(tile);
    this.cb.onTileTap();

    const trayX = this.getTraySlotX(trayIndex);
    const trayY = this.getTrayY();

    if (tile.gameObject) {
      tile.gameObject.setDepth(100 + trayIndex);
      this.tweens.add({
        targets: tile.gameObject, x: trayX, y: trayY,
        duration: 200, ease: 'Power2',
        onComplete: () => this.checkTrayMatches(),
      });
    }
    this.updateClickableState();
  }

  private getTraySlotX(index: number): number {
    const { width } = this.scale;
    const s = this.tileSize;
    const totalW = TRAY_SIZE * (s + this.tileGap) - this.tileGap;
    return width / 2 - totalW / 2 + s / 2 + index * (s + this.tileGap);
  }

  private getTrayY(): number {
    return this.scale.height - 80;
  }

  private checkTrayMatches(): void {
    const groups = new Map<number, Tile[]>();
    for (const t of this.traySlots) {
      const key = t.shape * 100 + t.color;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(t);
    }

    for (const [, tiles] of groups) {
      if (tiles.length >= 3) {
        const toClear = tiles.slice(0, 3);
        for (const tile of toClear) {
          tile.cleared = true;
          if (tile.gameObject) {
            this.tweens.add({
              targets: tile.gameObject, alpha: 0, scale: 0.5, duration: 250,
              onComplete: () => tile.gameObject?.destroy(),
            });
          }
          const idx = this.traySlots.indexOf(tile);
          if (idx >= 0) this.traySlots.splice(idx, 1);
        }
        this.score += 30;
        this.updateScore();
        this.cb.onTileClear();
        break;
      }
    }

    this.repositionTray();
    this.time.delayedCall(300, () => {
      this.updateClickableState();
      this.checkGameEnd();
    });
  }

  private repositionTray(): void {
    for (let i = 0; i < this.traySlots.length; i++) {
      const tile = this.traySlots[i];
      if (tile.gameObject) {
        this.tweens.add({
          targets: tile.gameObject,
          x: this.getTraySlotX(i), y: this.getTrayY(), duration: 150,
        });
      }
    }
  }

  private checkGameEnd(): void {
    const remaining = this.tiles.filter((t) => !t.cleared);
    if (remaining.length === 0 && !this.solved) {
      this.solved = true;
      this.playing = false;
      this.score += 100; // level bonus
      this.updateScore();

      // Check if there's a next level
      if (this.level < LEVELS.length) {
        this.cb.onLevelComplete(this.level);
        this.showLevelCompleteOverlay(this.level + 1);
      } else {
        // All levels complete
        const prevStreak = getStoredStreak();
        saveStreak(prevStreak + 1);
        saveBest(100);
        this.cb.onPuzzleSolved(this.level, this.score);
        this.showGameCompleteOverlay();
      }
      return;
    }

    if (this.traySlots.length >= TRAY_SIZE && !this.solved) {
      const groups = new Map<number, number>();
      for (const t of this.traySlots) {
        const key = t.shape * 100 + t.color;
        groups.set(key, (groups.get(key) || 0) + 1);
      }
      const hasMatch = Array.from(groups.values()).some((c) => c >= 3);
      if (!hasMatch) {
        this.playing = false;
        saveStreak(0);
        const clearedCount = this.tiles.filter((t) => t.cleared).length;
        const pct = Math.round((clearedCount / this.tiles.length) * 100);
        saveBest(pct);
        this.cb.onPuzzleFailed(pct, this.score);
        this.showGameOverOverlay(pct);
      }
    }
  }

  private buildTrayBackground(): void {
    const { width } = this.scale;
    const s = this.tileSize;
    const trayY = this.getTrayY();
    const trayBg = this.add.rectangle(
      width / 2, trayY,
      TRAY_SIZE * (s + this.tileGap) + 20, s + 20,
      COLOR_TRAY_BG, 0.95,
    );
    trayBg.setStrokeStyle(2, 0x3a3a5a, 0.6);
    trayBg.setDepth(50);

    const trayLabel = this.add.text(width / 2, trayY - s / 2 - 14, 'TRAY', {
      fontSize: '10px', color: '#555577', fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold', letterSpacing: 2,
    });
    trayLabel.setOrigin(0.5);
  }

  private buildStartOverlay(): void {
    const { width, height } = this.scale;
    const overlay = this.add.container(width / 2, height / 2);
    overlay.setDepth(1000);

    const bg = this.add.rectangle(0, 0, width, height, 0x0a0a18, 0.9);
    bg.setOrigin(0.5);
    overlay.add(bg);

    const title = this.add.text(0, -100, 'DAILY TILE MATCH', {
      fontSize: '28px', color: COLOR_TEXT, fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif', letterSpacing: 3,
    });
    title.setOrigin(0.5);
    overlay.add(title);

    const rules = this.add.text(0, -40, [
      'Tap tiles to move them to the tray.',
      'Match 3 identical tiles to clear.',
      'If the tray fills up — you lose!',
      'Clear all tiles to advance to next level.',
    ].join('\n'), {
      fontSize: '14px', color: COLOR_TEXT_DIM,
      fontFamily: 'Arial, sans-serif', align: 'center', lineSpacing: 8,
    });
    rules.setOrigin(0.5);
    overlay.add(rules);

    const streak = getStoredStreak();
    const streakText = this.add.text(0, 30, `Streak: ${streak} day${streak !== 1 ? 's' : ''}`, {
      fontSize: '15px', color: '#ff6b9d', fontFamily: 'Arial, sans-serif', fontStyle: 'bold',
    });
    streakText.setOrigin(0.5);
    overlay.add(streakText);

    const btn = this.add.rectangle(0, 90, 200, 56, COLOR_ACCENT);
    btn.setStrokeStyle(2, 0xffffff, 0.15);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => {
      overlay.destroy();
      this.startGame();
    });
    overlay.add(btn);

    const btnText = this.add.text(0, 90, 'PLAY', {
      fontSize: '20px', color: '#ffffff', fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif', letterSpacing: 1,
    });
    btnText.setOrigin(0.5);
    overlay.add(btnText);
  }

  private showLevelCompleteOverlay(nextLevel: number): void {
    const { width, height } = this.scale;
    const overlay = this.add.container(width / 2, height / 2);
    overlay.setDepth(1000);

    const bg = this.add.rectangle(0, 0, width, height, 0x0a0a18, 0.9);
    bg.setOrigin(0.5);
    overlay.add(bg);

    const title = this.add.text(0, -60, `LEVEL ${this.level} COMPLETE!`, {
      fontSize: '28px', color: COLOR_SUCCESS, fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    title.setOrigin(0.5);
    overlay.add(title);

    const sub = this.add.text(0, -10, `Score: ${this.score}`, {
      fontSize: '18px', color: COLOR_TEXT_DIM, fontFamily: 'Arial, sans-serif',
    });
    sub.setOrigin(0.5);
    overlay.add(sub);

    const btn = this.add.rectangle(0, 50, 220, 56, COLOR_ACCENT);
    btn.setStrokeStyle(2, 0xffffff, 0.15);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => {
      overlay.destroy();
      this.nextLevel();
    });
    overlay.add(btn);

    const btnText = this.add.text(0, 50, `LEVEL ${nextLevel} →`, {
      fontSize: '18px', color: '#ffffff', fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    btnText.setOrigin(0.5);
    overlay.add(btnText);
  }

  private showGameCompleteOverlay(): void {
    const { width, height } = this.scale;
    const overlay = this.add.container(width / 2, height / 2);
    overlay.setDepth(1000);

    const bg = this.add.rectangle(0, 0, width, height, 0x0a0a18, 0.9);
    bg.setOrigin(0.5);
    overlay.add(bg);

    const title = this.add.text(0, -80, 'ALL LEVELS COMPLETE!', {
      fontSize: '26px', color: COLOR_SUCCESS, fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    title.setOrigin(0.5);
    overlay.add(title);

    const sub = this.add.text(0, -20, `Final Score: ${this.score}`, {
      fontSize: '20px', color: COLOR_TEXT, fontFamily: 'Arial, sans-serif',
    });
    sub.setOrigin(0.5);
    overlay.add(sub);

    const btn = this.add.rectangle(0, 50, 200, 56, COLOR_ACCENT);
    btn.setStrokeStyle(2, 0xffffff, 0.15);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.scene.restart());
    overlay.add(btn);

    const btnText = this.add.text(0, 50, 'PLAY AGAIN', {
      fontSize: '18px', color: '#ffffff', fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    btnText.setOrigin(0.5);
    overlay.add(btnText);
  }

  private showGameOverOverlay(pct: number): void {
    const { width, height } = this.scale;
    const overlay = this.add.container(width / 2, height / 2);
    overlay.setDepth(1000);

    const bg = this.add.rectangle(0, 0, width, height, 0x0a0a18, 0.9);
    bg.setOrigin(0.5);
    overlay.add(bg);

    const title = this.add.text(0, -80, 'GAME OVER', {
      fontSize: '32px', color: COLOR_DANGER, fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    title.setOrigin(0.5);
    overlay.add(title);

    const sub = this.add.text(0, -20, `Level ${this.level} • Cleared: ${pct}%`, {
      fontSize: '16px', color: COLOR_TEXT_DIM, fontFamily: 'Arial, sans-serif',
    });
    sub.setOrigin(0.5);
    overlay.add(sub);

    const best = this.add.text(0, 10, `Best: ${getBest()}%  •  Score: ${this.score}`, {
      fontSize: '14px', color: COLOR_TEXT_DIM, fontFamily: 'Arial, sans-serif',
    });
    best.setOrigin(0.5);
    overlay.add(best);

    const btn = this.add.rectangle(0, 70, 200, 56, COLOR_ACCENT);
    btn.setStrokeStyle(2, 0xffffff, 0.15);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.scene.restart());
    overlay.add(btn);

    const btnText = this.add.text(0, 70, 'TRY AGAIN', {
      fontSize: '18px', color: '#ffffff', fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    btnText.setOrigin(0.5);
    overlay.add(btnText);
  }

  private startGame(): void {
    this.playing = true;
    this.buildBoard();
    this.cb.onStart();
  }

  private nextLevel(): void {
    this.level++;
    this.solved = false;
    this.playing = true;

    // Clear old tiles
    for (const tile of this.tiles) {
      tile.gameObject?.destroy();
    }
    this.tiles = [];
    this.traySlots = [];

    // Update header
    const levelText = this.children.list.find((c) => c instanceof Phaser.GameObjects.Text && c.text.includes('LEVEL')) as Phaser.GameObjects.Text;
    if (levelText) levelText.setText(`LEVEL ${this.level}`);

    this.buildBoard();
  }

  requestBegin(): void {
    // Wait for user click
  }
}

export class DailyTileMatch implements GameRuntime {
  private readonly config: GameRuntimeConfig;
  private readonly events: GameRuntimeEvents;
  private state: GameRuntimeState = 'idle';
  private score = 0;
  private game: Phaser.Game | null = null;
  private scene: DailyTileMatchScene | null = null;

  constructor(config: GameRuntimeConfig, events: GameRuntimeEvents = {}) {
    this.config = config;
    this.events = events;
  }

  mount(): void {
    if (this.state !== 'idle') return;

    this.scene = new DailyTileMatchScene({
      onTileTap: () => this.events.onEvent?.('tile_tap', {}),
      onTileClear: () => {
        this.score += 30;
        this.events.onScoreChange?.(this.score);
        this.events.onEvent?.('tile_clear', {});
      },
      onPuzzleSolved: (level: number, score: number) => {
        this.state = 'gameover';
        this.events.onGameOver?.(score, { solved: true, level });
        this.events.onEvent?.('puzzle_solved', { level, score });
      },
      onPuzzleFailed: (pct: number, score: number) => {
        this.state = 'gameover';
        this.events.onGameOver?.(score, { solved: false, pct });
        this.events.onEvent?.('puzzle_failed', { pct });
      },
      onStart: () => {
        this.state = 'running';
        this.events.onStart?.();
      },
      onLevelComplete: (level: number) => {
        this.events.onEvent?.('level_complete', { level });
      },
    });

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      backgroundColor: COLOR_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        width: GAME_WIDTH,
        height: GAME_HEIGHT,
      },
      scene: [this.scene],
    });

    this.state = 'mounted';
  }

  start(): void {
    if (this.state === 'mounted' || this.state === 'paused' || this.state === 'gameover') {
      this.state = 'running';
      this.scene?.requestBegin();
    }
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.scene?.scene?.pause();
    this.events.onPause?.();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.scene?.scene?.resume();
    this.events.onResume?.();
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    this.game?.destroy(true);
    this.game = null;
    this.scene = null;
    this.state = 'destroyed';
  }

  getScore(): number { return this.score; }
  getState(): GameRuntimeState { return this.state; }

  retry(): void {
    this.score = 0;
    this.events.onScoreChange?.(this.score);
    this.state = 'running';
    this.scene?.requestBegin();
  }
}

export default DailyTileMatch;
